//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useContext, useEffect, useState } from 'react';
import { convertStylesToStyled } from '@/_utils/utils';
import { TitleEntity } from '@/redux/slices/slice_types';
import styled, { Theme } from '@emotion/styled';
import baseAxios from 'axios';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import axios from '../../../../_utils/axios';
import { getTmdbSearchQuery, selectTmdbTitle } from '../../../../_utils/titleUtils';
import { tmdb_multi_search } from '../../../../constants/constants';
import { ConfigContext } from '../../../../contextAPI/configContext';
import { selectJob } from '../../../../redux/selectors/jobSelectors';
import { Spinner } from '../../../_common';
import JobTitleCardImage from './JobTitleCardImage';
import JobTitleCardOverlay from './JobTitleCardOverlay';
import ProxyVideo from './ProxyVideo';
import { useAppSelector } from '@/redux/hooks';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OmakasePlayerClientConfig } from '@/__configs/config_types';

const JobTitleCard = () => {
  const { id } = useParams() as { id: string };
  const { title = {} } = useAppSelector(selectJob);
  const disableTMDB = useContext(ConfigContext)?.titleConfig?.disableTMDB;
  const [tmdbTitle, setTmdbTitle] = useState<TitleEntity | undefined | null>(null);
  const [loading, setLoading] = useState(true);
  const [playingVideo, playVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [proxyImg, setProxyImg] = useState<string | null>(null);
  const [checkingProxy, setCheckingProxy] = useState(false);
  const disableJobDetailProxy = useContext(ConfigContext)?.disableJobDetailProxy;
  const omakaseFeatures = useConfigValue<OmakasePlayerClientConfig['features']>(['omakasePlayerConfig', 'features']);
  const isAdvancedPlayerPlaying = !!omakaseFeatures?.length && playingVideo;

  const setDebouncedLoading = (state: boolean, immediate = false) => {
    if (immediate) setLoading(state);
    const makeDebounce = debounce(() => {
      setLoading(state);
    }, 2000);
    makeDebounce();
  };

  useEffect(() => {
    if (!id || disableJobDetailProxy) return;
    setCheckingProxy(true);
    const controller = new AbortController();
    axios
      .get(`/core/jobs/${id}/proxy/`, { signal: controller.signal })
      .then(({ data }) => {
        setVideoUrl(data?.message?.url);
        setProxyImg(data?.message?.thumbnail_url);
        setCheckingProxy(false);
      })
      .catch(() => setCheckingProxy(false));
    return () => {
      setCheckingProxy(false);
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    if (disableTMDB) return setDebouncedLoading(false);
    fetchTmdb();
  }, [title]);

  const fetchTmdb = () => {
    const searchQuery = getTmdbSearchQuery(title);
    if (searchQuery) {
      const year = title.release_year;
      baseAxios
        .get(tmdb_multi_search(searchQuery, title.entity_type, year))
        .then((res) => setTmdbTitle(selectTmdbTitle(res.data?.results)))
        .then(() => setDebouncedLoading(false))
        .catch(() => setDebouncedLoading(false, true));
    } else {
      setDebouncedLoading(false, true);
    }
  };

  const toggleVideo = () => playVideo(!playingVideo);

  return (
    <StyledJobTitleCardDiv playing={playingVideo} isAdvancedPlayerPlaying={isAdvancedPlayerPlaying}>
      {!isAdvancedPlayerPlaying && <StyledGradientDiv />}
      {loading && <Spinner />}

      {playingVideo && !disableJobDetailProxy ? (
        <ProxyVideo id={`job-title-video-${id}`} toggleVideo={toggleVideo} url={videoUrl as string} poster={proxyImg as string} />
      ) : (
        <>
          <JobTitleCardOverlay
            tmdbTitle={tmdbTitle}
            disableProxyButton={!videoUrl}
            hideProxyButton={disableJobDetailProxy}
            toggleVideo={toggleVideo}
            checkingProxy={checkingProxy}
          />
          {!loading && <JobTitleCardImage tmdbPath={tmdbTitle?.backdrop_path} proxyImg={proxyImg} />}
        </>
      )}
    </StyledJobTitleCardDiv>
  );
};

export default JobTitleCard;

type StyledJobTitleCardDivProps = {
  theme?: Theme;
  playing: boolean;
  isAdvancedPlayerPlaying: boolean;
};

const StyledJobTitleCardDiv = styled.div(
  ({ theme, playing, isAdvancedPlayerPlaying }: StyledJobTitleCardDivProps) => `
  margin-top: ${theme?.spacing(3)};
  margin-bottom: ${theme?.spacing(5)};
  height: auto;
  max-height: 420px;
  min-height: 420px;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  background: ${isAdvancedPlayerPlaying ? 'none' : theme?.palette.primary.dark};
  transition: ease all 250ms;
  userSelect: none;
  ${convertStylesToStyled(theme?.customOverrides?.JobTitleCard)}

  ${
    playing
      ? `
    max-height: ${isAdvancedPlayerPlaying ? 'unset' : '999px'};
    transition: ease all 500ms;
  `
      : null
  };
`
);

const StyledGradientDiv = styled.div`
  width: 100%;
  height: 80%;
  z-index: 0;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,.8));
`;